import type { Locale } from '@/utils/localeFormatters';

import type { ChartTheme } from '../theme';

import { formatAxisValue, resolveTickDecimals } from './axisFormat';
import type { MetricAxisSpec } from './axisTypes';
import {
    CHART_AXIS_BORDER_MARGIN,
    CHART_GRID_DASH_PATTERN,
    CHART_GRID_LINE_WIDTH,
    DEFAULT_MAX_TICKS_LIMIT,
} from './constants';
import type { MetricScaleOptions } from './optionTypes';

/**
 * A value (y) axis of a metric chart. Split out of `buildChartOptions` to keep that file about
 * assembling the options.
 */

function toTickNumber(tickValue: string | number): number {
    return typeof tickValue === 'string' ? Number(tickValue) : tickValue;
}

/**
 * Formats a tick, returning an empty string when rounding would repeat the
 * previous label (e.g. a 0–1 growth axis with whole-unit ticks collapsing to
 * "0 g", "0 g", "1 g", "1 g"). Chart.js still draws the grid line; only the
 * duplicate text is suppressed.
 */
function formatUniqueTickLabel(
    tickValue: string | number,
    index: number,
    ticks: ReadonlyArray<{ value: number }>,
    axis: MetricAxisSpec,
    locale: Locale
): string {
    const decimals = resolveTickDecimals(axis);
    const label = formatAxisValue(toTickNumber(tickValue), axis, locale, decimals, 'tick');
    if (index > 0) {
        const previous = formatAxisValue(ticks[index - 1].value, axis, locale, decimals, 'tick');
        if (label === previous) return '';
    }
    return label;
}

/** The parts of a Chart.js scale the alignment reads and writes; a `Scale` is one. */
export interface TickAlignedAxis {
    chart: {
        scales: Record<
            string,
            { min: number; max: number; ticks: { value: number }[] } | undefined
        >;
    };
    min: number;
    max: number;
    ticks: { value: number }[];
}

/**
 * Puts a right axis' ticks on the left axis' grid lines: each left tick maps to the value at
 * the same height on this axis, so both scales read against one set of lines.
 */
export function alignTicksToLeftAxis(axis: TickAlignedAxis): void {
    const left = axis.chart.scales.yAxisLeft;
    if (!left || left.ticks.length === 0) return;
    const leftRange = left.max - left.min;
    const range = axis.max - axis.min;
    if (leftRange === 0 || range === 0) return;
    axis.ticks = left.ticks.map((tick) => ({
        value: axis.min + ((tick.value - left.min) / leftRange) * range,
    }));
}

/**
 * `values` are those of the visible series plotted on this axis, which `fitRange` turns into
 * its bounds.
 */
export function buildAxisScale(
    axis: MetricAxisSpec,
    position: 'left' | 'right',
    locale: Locale,
    theme: ChartTheme,
    values: number[] = [],
    gridLines: 'dashed' | 'solid' = 'dashed'
): MetricScaleOptions {
    const fitted = axis.fitRange?.(values);
    // The only thing tying an overlay's scale to its series is the colour, so an axis that
    // names one keeps it (lifted like the series' own); every other axis takes the theme's.
    const color = axis.color ? theme.color(axis.color) : theme.text;
    return {
        type: 'linear' as const,
        // An overlay axis can scale its series without drawing a scale of its own,
        // which is what keeps a chart with three overlays from becoming all axis.
        display: axis.hidden !== true,
        position,
        stacked: axis.stacked ?? false,
        reverse: axis.reverse ?? false,
        beginAtZero: axis.beginAtZero ?? false,
        title: {
            display: !!axis.label,
            text: axis.label,
            color,
        },
        min: fitted ? fitted.min : axis.min,
        max: fitted ? fitted.max : axis.max,
        suggestedMin: axis.suggestedMin,
        suggestedMax: axis.suggestedMax,
        ...(axis.grace === undefined ? {} : { grace: axis.grace }),
        ticks: {
            stepSize: axis.stepSize,
            maxTicksLimit: axis.maxTicksLimit ?? DEFAULT_MAX_TICKS_LIMIT,
            ...(axis.autoSkip === undefined ? {} : { autoSkip: axis.autoSkip }),
            ...(axis.tickPadding === undefined ? {} : { padding: axis.tickPadding }),
            color,
            callback: (
                tickValue: string | number,
                index: number,
                ticks: ReadonlyArray<{ value: number }>
            ): string => formatUniqueTickLabel(tickValue, index, ticks, axis, locale),
        },
        border:
            gridLines === 'solid'
                ? { color: theme.axis }
                : { dash: CHART_GRID_DASH_PATTERN, display: false },
        grid: {
            // Only one axis may draw grid lines across the plot area, otherwise the
            // two sets overlap at different intervals. Left wins by default.
            drawOnChartArea: position === 'left' ? axis.grid !== false : axis.grid === true,
            color: theme.grid,
            ...(gridLines === 'solid'
                ? {}
                : { lineWidth: CHART_GRID_LINE_WIDTH, ...CHART_AXIS_BORDER_MARGIN }),
        },
        ...(position === 'right' && axis.alignTicksToLeft
            ? { afterBuildTicks: alignTicksToLeftAxis }
            : {}),
    };
}
